import * as React from "react";
import "./annex.css";

// Underground window chrome — the Annex's own dull/rugged program
// windows (riveted-steel / label-tape / dead-CRT-bezel headers), each
// with its own minimize control. Replaces the surface WindowsCard/MacCard
// wrappers for the Annex's mounted PROGRAM windows.
import AnnexWindow from "../AnnexWindow/AnnexWindow";

// New feature components (self-contained, untouched)
import CrtTerminal from "../CrtTerminal/CrtTerminal";
import Pinboard from "../Pinboard/Pinboard";
import AmbulationField from "../AmbulationField/AmbulationField";
import TheEye from "../TheEye/TheEye";
import SprayCanvas from "../SprayCanvas/SprayCanvas";
import ReceiptGuestbook from "../ReceiptGuestbook/ReceiptGuestbook";
import CassetteDeck from "../CassetteDeck/CassetteDeck";

// New derived features (v2)
import EyeWall from "../EyeWall/EyeWall";
import RedString from "../RedString/RedString";
import Prescription from "../Prescription/Prescription";
import DeadChannel from "../DeadChannel/DeadChannel";

// Cross-window programs (v3) — these talk to EACH OTHER between separate
// browser windows / tabs over _shared/windowBus. PatchBay + Transmitter are
// SENDERS; PowerMonitor is the RECEIVER they drive live across the gap.
import PatchBay from "../PatchBay/PatchBay";
import PowerMonitor from "../PowerMonitor/PowerMonitor";
import Transmitter from "../Transmitter/Transmitter";

// Rivet rotations seeded ONCE at module load (not per render) so they don't
// jitter on every re-render. A small fixed pool of slightly-skewed bolts.
const RIVET_ROT = [6, 18, 11, 22, 3, 14, 9, 19];

function Rivet(props) {
    const deg = RIVET_ROT[(props.i || 0) % RIVET_ROT.length];
    return (
        <div className="rivot" style={{ transform: "rotateZ(" + deg + "deg)" }} />
    );
}

// THE ANNEX — a second wing of the ideaboard, consolidated into clean sections.
//
// Layout reads as ONE deliberate underground hall:
//   1. brushed-metal title bar (double-vision marquee, rivets)
//   2. a full-width EVIDENCE band: the Pinboard + the RedString corkboard,
//      the two bare wall surfaces, side by side as the wing's "back wall"
//   3. a single uniform CARD GRID of the mounted programs, on a steady gutter
//   4. ONE persistent RETURN-TO-THE-SURFACE control, pinned bottom-LEFT so it
//      never collides with the PlayerDock in the lower-right.
//
// All 11 features stay mounted; this is layout/visual consolidation only.
function Annex(props) {
    const sound = props.soundEnabled;

    // Scroll back up to the surface (the main Ambulation Zone board). We offer a
    // smooth glide, but — exactly like the descend path — a smooth scroll over
    // the long distance up to the top can be interrupted or silently no-op, so
    // we ALWAYS re-assert an INSTANT jump to 0 on the next frame and a short
    // fallback timeout, so the visitor reliably reaches the surface.
    function returnToSurface() {
        var reduce =
            typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } else {
            window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        }
        var landAtTop = function () {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        };
        if (typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(landAtTop);
        }
        setTimeout(landAtTop, 700);
    }

    function onSurfaceKey(e) {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
            e.preventDefault();
            returnToSurface();
        }
    }

    return (
        <section className="annex-page" aria-label="The Annex" ref={props.annexRef}>
            {/* ---- 1. Title bar ---- */}
            <div className="annex-header-wrap">
                <div className="annex-bolt-wrapper">
                    <Rivet i={0} />
                    <Rivet i={1} />
                </div>
                <div className="annex-title-wrap">
                    <div className="annex-double-vision">
                        <div className="annex-title-text">
                            <span className="annex-title-span">- THE ANNEX // SECOND WING -</span>
                        </div>
                    </div>
                </div>
                <div className="annex-bolt-wrapper">
                    <Rivet i={2} />
                    <Rivet i={3} />
                </div>
            </div>

            {/* Centered max-width hall so the wing reads as a composed room */}
            <div className="annex-board">
                {/* ---- 2. Evidence band — the wing's back wall ----
                    Pinboard + RedString are their OWN bare surfaces (no window
                    chrome) so their pinned photos / strings hang free without
                    being clipped. Grouped into one labeled section so they read
                    as a deliberate pairing rather than two loose slabs. */}
                <div className="annex-section annex-section--wall">
                    <div className="annex-section-label">// EVIDENCE WALL</div>
                    <div className="annex-wall-grid">
                        <div className="annex-wall-slot">
                            <Pinboard />
                        </div>
                        <div className="annex-wall-slot annex-wall-slot--strings">
                            <RedString />
                        </div>
                    </div>
                </div>

                {/* ---- 3. Program grid — the mounted features ----
                    Every feature shares one footprint + one gutter so the
                    Annex's dull/rugged window chrome lines up into a single
                    clean grid. Each PROGRAM is wrapped in an AnnexWindow whose
                    `variant` (1=riveted steel bar, 2=worn label-tape tag,
                    3=scratched dead-CRT bezel) rotates 1,2,3,1,2,3,... so all
                    three header characters appear across the wing. Every
                    AnnexWindow carries its own minimize control in its header. */}
                <div className="annex-section annex-section--programs">
                    <div className="annex-section-label">// PROGRAMS</div>
                    <div className="annex-windows">
                        <AnnexWindow
                            id="annex-crt-term"
                            variant={1}
                            className="window-item"
                            programText="AMBULATE.EXE"
                            artComp={<CrtTerminal sound={sound} />}
                        />

                        <AnnexWindow
                            id="annex-field"
                            variant={2}
                            className="window-item"
                            programText="ambulation-field.p5"
                            artComp={<AmbulationField />}
                        />

                        <AnnexWindow
                            id="annex-the-eye"
                            variant={3}
                            className="window-item"
                            programText="THE EYE"
                            artComp={<TheEye />}
                        />

                        <AnnexWindow
                            id="annex-eyewall"
                            variant={1}
                            className="window-item"
                            programText="WATCHERS.EXE"
                            artComp={<EyeWall />}
                        />

                        <AnnexWindow
                            id="annex-spray"
                            variant={2}
                            className="window-item"
                            programText="SPRAY.EXE"
                            artComp={<SprayCanvas />}
                        />

                        <AnnexWindow
                            id="annex-cassette"
                            variant={3}
                            className="window-item"
                            programText="TAPE DECK // FOUND RECORDINGS"
                            artComp={<CassetteDeck sound={sound} />}
                        />

                        <AnnexWindow
                            id="annex-guestbook"
                            variant={1}
                            className="window-item"
                            programText="GUESTBOOK.RCP"
                            artComp={<ReceiptGuestbook />}
                        />

                        <AnnexWindow
                            id="annex-pharmacy"
                            variant={2}
                            className="window-item annex-card--rx"
                            programText="PHARMACY.RCP"
                            artComp={<Prescription />}
                        />

                        <AnnexWindow
                            id="annex-deadchannel"
                            variant={3}
                            className="window-item"
                            programText="DEAD-CHANNEL"
                            artComp={<DeadChannel sound={sound} />}
                        />

                        {/* ---- Cross-window rig ----
                            These three TALK ACROSS WINDOWS over windowBus.
                            Open the Annex in two browser windows: throw a rail
                            on the PATCHBAY (or turn the TRANSMITTER dial / fire
                            a specimen) in one window and the POWER-MONITOR in
                            the OTHER window lights, swings its needle, and shows
                            the inbound specimen — live. */}
                        <AnnexWindow
                            id="annex-patchbay"
                            variant={1}
                            className="window-item"
                            programText="PATCHBAY.SYS"
                            artComp={<PatchBay sound={sound} />}
                        />

                        <AnnexWindow
                            id="annex-transmitter"
                            variant={2}
                            className="window-item"
                            programText="TRANSMIT.SYS"
                            artComp={<Transmitter sound={sound} />}
                        />

                        <AnnexWindow
                            id="annex-powermonitor"
                            variant={3}
                            className="window-item"
                            programText="MONITOR.OUT"
                            artComp={<PowerMonitor sound={sound} />}
                        />
                    </div>
                </div>

                {/* ---- End-of-wing seal ----
                    A slim inline closing bar that visibly SEALS the wing right
                    under the last program window, so the Annex ends cleanly with
                    no empty brick expanse trailing below. It also carries an
                    inline return affordance (the fixed corner EXIT sign stays). */}
                <div
                    className="annex-end-seal"
                    role="button"
                    tabIndex={0}
                    aria-label="End of the Annex — return to the surface"
                    onClick={returnToSurface}
                    onKeyDown={onSurfaceKey}
                >
                    <span className="annex-end-seal-rule" />
                    <span className="annex-end-seal-text">↑ END OF WING — RETURN TO THE SURFACE</span>
                    <span className="annex-end-seal-rule" />
                </div>
            </div>

            {/* ---- 4. The ONE return control ----
                A single persistent "return to the surface" sign pinned to the
                lower-LEFT (opposite the PlayerDock) so it's always reachable
                while scrolling and never sits under the dock. */}
            <div
                className="annex-corner-exit"
                role="button"
                tabIndex={0}
                aria-label="Return to the surface — back to the Ambulation Zone"
                onClick={returnToSurface}
                onKeyDown={onSurfaceKey}
            >
                <span className="annex-corner-exit-arrow">↑</span>
                <span className="annex-corner-exit-text">
                    <span className="annex-corner-exit-main">RETURN TO THE SURFACE</span>
                    <span className="annex-corner-exit-sub">back to the Ambulation Zone</span>
                </span>
            </div>
        </section>
    );
}

export default Annex;
